/** Keystone (perspective) correction math for the photo-tracing pipeline
 *  (issue #486, parent #75).
 *
 *  The user marks the 4 corners of something rectangular in their photo
 *  (a cutting mat, a sheet of paper, the leather piece itself); the
 *  projective transform mapping a unit square onto that quad lets us
 *  resample the photo into a de-skewed rectangle.
 *
 *  The math is Heckbert's classic unit-square → quadrilateral mapping
 *  (Fundamentals of Texture Mapping and Image Warping, 1989) — closed
 *  form, no linear solver needed for the 4-point case. All of this file
 *  is pure (no DOM) so it unit-tests under jsdom; the canvas resampling
 *  lives in warpQuadToRect's caller-supplied ImageData buffers.
 */

export interface Point {
  x: number;
  y: number;
}

/** Quad corners in image pixel coordinates, in TL, TR, BR, BL order. */
export type Quad = [Point, Point, Point, Point];

/** Row-major 3×3 projective transform. */
export type Matrix3 = [
  number, number, number,
  number, number, number,
  number, number, number,
];

/** Projective map taking the unit square (u,v) ∈ [0,1]² onto `quad`
 *  (TL ↦ (0,0), TR ↦ (1,0), BR ↦ (1,1), BL ↦ (0,1)). */
export function unitSquareToQuad(quad: Quad): Matrix3 {
  const [p0, p1, p2, p3] = quad;
  // Heckbert's notation: accumulate the "excess" of the quad over a
  // parallelogram; zero excess means the map is plain affine.
  const sx = p0.x - p1.x + p2.x - p3.x;
  const sy = p0.y - p1.y + p2.y - p3.y;
  if (Math.abs(sx) < 1e-12 && Math.abs(sy) < 1e-12) {
    return [
      p1.x - p0.x, p3.x - p0.x, p0.x,
      p1.y - p0.y, p3.y - p0.y, p0.y,
      0, 0, 1,
    ];
  }
  const dx1 = p1.x - p2.x;
  const dy1 = p1.y - p2.y;
  const dx2 = p3.x - p2.x;
  const dy2 = p3.y - p2.y;
  const det = dx1 * dy2 - dx2 * dy1;
  const g = (sx * dy2 - dx2 * sy) / det;
  const h = (dx1 * sy - sx * dy1) / det;
  return [
    p1.x - p0.x + g * p1.x, p3.x - p0.x + h * p3.x, p0.x,
    p1.y - p0.y + g * p1.y, p3.y - p0.y + h * p3.y, p0.y,
    g, h, 1,
  ];
}

/** Apply a 3×3 projective transform to a point. */
export function applyMatrix3(m: Matrix3, p: Point): Point {
  const w = m[6] * p.x + m[7] * p.y + m[8];
  return {
    x: (m[0] * p.x + m[1] * p.y + m[2]) / w,
    y: (m[3] * p.x + m[4] * p.y + m[5]) / w,
  };
}

/** Invert a 3×3 projective transform (adjugate over determinant). */
export function invertMatrix3(m: Matrix3): Matrix3 {
  const [a, b, c, d, e, f, g, h, i] = m;
  const A = e * i - f * h;
  const B = c * h - b * i;
  const C = b * f - c * e;
  const det = a * A + d * B + g * C;
  return [
    A / det, B / det, C / det,
    (f * g - d * i) / det, (a * i - c * g) / det, (c * d - a * f) / det,
    (d * h - e * g) / det, (b * g - a * h) / det, (a * e - b * d) / det,
  ];
}

/** Where the WHOLE source image lands after rectification (issue #492):
 *  the bounding box, in de-skewed pixels (the space where `quad` maps
 *  onto a `rectWidth`×`rectHeight` rectangle at the origin), of the
 *  source image's four corners pushed through the inverse keystone map.
 *  Always contains the quad's own rectangle; the extra extent is the
 *  rest of the photo, so the calibration step can show all of it
 *  instead of cropping to the quad. */
export function fullDeskewBounds(
  quad: Quad,
  imageWidth: number,
  imageHeight: number,
  rectWidth: number,
  rectHeight: number,
): { x: number; y: number; w: number; h: number } {
  const inv = invertMatrix3(unitSquareToQuad(quad));
  const corners: Point[] = [
    { x: 0, y: 0 },
    { x: imageWidth, y: 0 },
    { x: imageWidth, y: imageHeight },
    { x: 0, y: imageHeight },
  ];
  let minX = 0;
  let minY = 0;
  let maxX = rectWidth;
  let maxY = rectHeight;
  for (const c of corners) {
    const uv = applyMatrix3(inv, c);
    const x = uv.x * rectWidth;
    const y = uv.y * rectHeight;
    minX = Math.min(minX, x);
    minY = Math.min(minY, y);
    maxX = Math.max(maxX, x);
    maxY = Math.max(maxY, y);
  }
  return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
}

/** Natural output size for de-skewing `quad`: opposite edge lengths
 *  averaged, optionally capped to `maxDimension` (preserving aspect). */
export function deskewSize(
  quad: Quad,
  maxDimension = Infinity,
): { width: number; height: number } {
  const [tl, tr, br, bl] = quad;
  const dist = (a: Point, b: Point) => Math.hypot(a.x - b.x, a.y - b.y);
  let width = (dist(tl, tr) + dist(bl, br)) / 2;
  let height = (dist(tl, bl) + dist(tr, br)) / 2;
  const scale = Math.min(1, maxDimension / Math.max(width, height));
  width = Math.max(1, Math.round(width * scale));
  height = Math.max(1, Math.round(height * scale));
  return { width, height };
}

/** Default corner placement for a fresh photo: inset 10% from each edge
 *  so all four handles are visibly inside the frame and graspable. */
export function defaultQuad(imageWidth: number, imageHeight: number): Quad {
  const ix = imageWidth * 0.1;
  const iy = imageHeight * 0.1;
  return [
    { x: ix, y: iy },
    { x: imageWidth - ix, y: iy },
    { x: imageWidth - ix, y: imageHeight - iy },
    { x: ix, y: imageHeight - iy },
  ];
}

/** The preimage of a crop window under the unit-square→quad map: the
 *  four source-image points that the crop rectangle's corners de-skew
 *  to. Warping THIS quad to a rect is exactly "de-skew with `quad`,
 *  then crop" in one resample — a homography is determined by its four
 *  corner correspondences, so the composition collapses (issue #489).
 *  `crop` and `frame` are in the same (de-skewed) coordinate space. */
export function cropPreimageQuad(
  quad: Quad,
  crop: { x: number; y: number; w: number; h: number },
  frameWidth: number,
  frameHeight: number,
): Quad {
  const m = unitSquareToQuad(quad);
  const at = (x: number, y: number) =>
    applyMatrix3(m, { x: x / frameWidth, y: y / frameHeight });
  return [
    at(crop.x, crop.y),
    at(crop.x + crop.w, crop.y),
    at(crop.x + crop.w, crop.y + crop.h),
    at(crop.x, crop.y + crop.h),
  ];
}

/** Resample `src` so that `quad` maps onto the full `out` rectangle —
 *  the de-skew itself. Inverse mapping with bilinear sampling: for every
 *  output pixel, find its source position through the unit-square→quad
 *  map and blend the 4 surrounding source pixels. Pixels falling outside
 *  the source image come out transparent. Works on raw ImageData buffers
 *  so the math stays testable and the caller owns all canvas handling. */
export function warpQuadToRect(
  src: ImageData,
  quad: Quad,
  out: ImageData,
): void {
  const m = unitSquareToQuad(quad);
  const { width: ow, height: oh, data: o } = out;
  const { width: sw, height: sh, data: s } = src;
  for (let y = 0; y < oh; y++) {
    // +0.5: sample at output pixel centers.
    const v = (y + 0.5) / oh;
    for (let x = 0; x < ow; x++) {
      const u = (x + 0.5) / ow;
      const w = m[6] * u + m[7] * v + m[8];
      const sxf = (m[0] * u + m[1] * v + m[2]) / w - 0.5;
      const syf = (m[3] * u + m[4] * v + m[5]) / w - 0.5;
      const x0 = Math.floor(sxf);
      const y0 = Math.floor(syf);
      const oi = (y * ow + x) * 4;
      if (x0 < -1 || y0 < -1 || x0 >= sw || y0 >= sh) {
        o[oi + 3] = 0;
        continue;
      }
      const fx = sxf - x0;
      const fy = syf - y0;
      // Clamp the 2×2 neighbourhood to the image so edge pixels blend
      // with themselves instead of wrapping or vanishing.
      const cx0 = Math.max(0, Math.min(sw - 1, x0));
      const cx1 = Math.max(0, Math.min(sw - 1, x0 + 1));
      const cy0 = Math.max(0, Math.min(sh - 1, y0));
      const cy1 = Math.max(0, Math.min(sh - 1, y0 + 1));
      const i00 = (cy0 * sw + cx0) * 4;
      const i10 = (cy0 * sw + cx1) * 4;
      const i01 = (cy1 * sw + cx0) * 4;
      const i11 = (cy1 * sw + cx1) * 4;
      for (let c = 0; c < 4; c++) {
        const top = s[i00 + c]! * (1 - fx) + s[i10 + c]! * fx;
        const bot = s[i01 + c]! * (1 - fx) + s[i11 + c]! * fx;
        o[oi + c] = top * (1 - fy) + bot * fy;
      }
    }
  }
}
